import { FormApi } from "final-form";
import { action, computed, observable } from "mobx";

import { clone, newGuid } from "@bps/common-utils";
import {
  compareDatesPredicate,
  DateTime,
  formatCalendarDate,
  ordinalNumber
} from "@bps/date-time";
import { IGroup, ThematicButtonData } from "@bps/fluent-ui";
import { Country } from "@libs/enums/country.enum.ts";
import { ClaimStatuses } from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  AccClinicalTab,
  AddDocumentDto,
  ClinicalWorkflowFormat,
  CodedFieldClinicalDataItemDto,
  CorrespondenceDirection,
  CorrespondenceStatus,
  CorrespondenceType,
  DiagnosisDataItemDto,
  DischargeDataItemDto,
  DocumentContentType,
  DocumentCreateOptions,
  DocumentDto,
  DocumentEnum,
  DocumentMetadataItem,
  DocumentSource,
  EncounterType,
  PermanentClinicalTab,
  StoreType
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import {
  TemplateRenderDto,
  TemplateRenderOptions
} from "@libs/gateways/document/DocumentGateway.dtos.ts";
import { getNewMetadata } from "@modules/clinical/utils/clinical.utils.ts";
import { DischargeStatus } from "@shared-types/clinical/discharge-status.enum.ts";
import { EncounterFormValues } from "@shared-types/clinical/encounter-values.interface.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import { ClaimAdjustment } from "@stores/acc/models/ClaimAdjustment.ts";
import { ClinicalTabItem } from "@stores/clinical/models/clinical-tab/ClinicalTabItem.ts";
import { ClinicalDataTypeName } from "@stores/clinical/models/clinical-tab/ClinicalTabName.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { sortByDischargeItemCreatedDateDesc } from "@stores/clinical/utils/clinical.utils.ts";

import { ConditionDetailsModel } from "../patient-record/components/claims/ConditionsSidePanelHelper.ts";
import { EncounterDiscardFormValues } from "../patient-record/components/clinical-form/EncounterDiscardFormValues.tsx";
import { ConditionConsultEncounterItem } from "../patient-record/components/condition-consults-export/ConditionConsultsExportDialog.tsx";
import { getSideOfBodyKey } from "../patient-record/components/medical-history/utils.ts";
import { setClaimTab } from "../patient-record/PatientRecord.utils.ts";
import { AppInsightsClinicalRecordHelper } from "./AppInsightsClinicalRecordHelper.ts";

export class PatientRecordScreenHelper extends AppInsightsClinicalRecordHelper {
  constructor(
    protected root: IRootStore,
    public clinicalRecord: ClinicalRecord,
    showEncounterExistsPrompt: boolean = false
  ) {
    super(root, clinicalRecord);
    this.setShowEncounterExistsPrompt(showEncounterExistsPrompt);
  }

  get multiProviderEnabled() {
    return this.core.hasPermissions(Permission.MultiProviderClaimsAllowed);
  }

  @observable claimAdjustment: ClaimAdjustment | undefined;

  @observable claimReviewClaim: Claim | undefined;

  get encounterId() {
    return this.clinicalRecord.encounterId;
  }

  encounterFormApi: FormApi<EncounterFormValues> | undefined;

  @observable selectedCondition: ConditionDetailsModel | undefined;

  get routing() {
    return this.root.routing;
  }

  get clinical() {
    return this.root.clinical;
  }

  get userExperience() {
    return this.root.userExperience;
  }

  get core() {
    return this.root.core;
  }

  get booking() {
    return this.root.booking;
  }

  get acc() {
    return this.root.acc;
  }

  get patientId() {
    return this.clinicalRecord.id;
  }

  get isDischargeCompleted() {
    if (this.clinicalRecord) {
      return (
        this.clinicalRecord.getDischargeStatus(
          this.clinicalRecord.openEncounter?.businessRole
        ) === DischargeStatus.Completed
      );
    }
    return false;
  }

  get isViewOnlyOrDischarged() {
    return this.clinicalRecord.isView || this.isDischargeCompleted;
  }

  get isViewOnly() {
    return this.clinicalRecord.isView;
  }

  get isRecordUpdate() {
    return this.clinicalRecord.isRecordUpdate;
  }

  getUpdatedDiagnosis = async (
    diagnosisCode: CodedFieldClinicalDataItemDto,
    isPrimaryDiagnosis?: boolean,
    diagnosisSideSelected?: string[]
  ) => {
    if (!this.clinicalRecord.episodeOfCare?.id) return;

    const diagnoses = (
      await this.clinical.getEpisodeOfCare(
        this.clinicalRecord.episodeOfCare?.id
      )
    ).diagnoses;

    let updatedDiagnosis: DiagnosisDataItemDto[] = diagnoses
      ? clone(diagnoses)
      : [];

    const primaryDiagnosis: DiagnosisDataItemDto = {
      diagnosisCode,
      diagnosisSide: getSideOfBodyKey(diagnosisSideSelected),
      isPrimaryDiagnosis
    };

    if (updatedDiagnosis && updatedDiagnosis.length > 1) {
      updatedDiagnosis.map(x => {
        if (x.isPrimaryDiagnosis === true) {
          return { ...primaryDiagnosis };
        }
        return x;
      });
    } else {
      updatedDiagnosis = [primaryDiagnosis];
    }
    return updatedDiagnosis;
  };

  fetchUserOnSecGroupId = async (id: string) => {
    //fetching User with SecGroupId
    const fetchUsers = await this.root.core.fetchUsers({
      privateSecGroupIds: id
    });
    if (fetchUsers.length > 0) {
      return fetchUsers[0];
    } else {
      return undefined;
    }
  };

  showPrimaryDiagnosis = async (isProcedure?: boolean) => {
    const episodeOfCare = this.clinicalRecord.episodeOfCare;

    const isPrivate = episodeOfCare?.isPrivate;

    const clinicalView = (
      await this.userExperience.getUserSetting(this.core.userId)
    )?.clinicalView;

    const isNoneWorkflow =
      !clinicalView?.clinicalWorkflow ||
      clinicalView?.clinicalWorkflow === ClinicalWorkflowFormat.None;
    return !!episodeOfCare && isPrivate && isNoneWorkflow && !isProcedure;
  };

  setEncounterFormApi = (formApi: FormApi<EncounterFormValues> | undefined) => {
    this.encounterFormApi = formApi;
  };

  @action clearCurrentTabsEncounterData = (contextId?: string) => {
    const type = this.clinicalRecord.patientRecordTab?.activeTab?.type;
    if (type) {
      this.clinicalRecord.patientRecordTab?.setIsDirty(false, {
        type,
        contextId
      });
    }
  };

  @action
  public setClaimAdjustment = (claimAdjustment?: ClaimAdjustment) => {
    this.claimAdjustment = claimAdjustment;
    if (this.clinicalRecord.patientRecordTab) {
      this.clinicalRecord.patientRecordTab.currentClinicalClaimAdjustmentId =
        claimAdjustment?.id || undefined;
    }

    setClaimTab(
      this.clinicalRecord,
      AccClinicalTab.Adjustment,
      !claimAdjustment
    );
  };

  @action
  public setClaimReviewTab = (claim?: Claim) => {
    this.claimReviewClaim = claim;
    if (this.clinicalRecord.patientRecordTab) {
      this.clinicalRecord.patientRecordTab.currentClaimReviewClaimId =
        claim?.id || undefined;
    }

    setClaimTab(this.clinicalRecord, AccClinicalTab.Review, !claim);
  };

  @computed
  public get isClaimReviewOpen() {
    return this.clinicalRecord.patientRecordTab?.hasTabTypeOpen(
      AccClinicalTab.Review
    );
  }

  @action
  setSelectedCondition = (condition: ConditionDetailsModel | undefined) => {
    this.selectedCondition = condition;
  };

  async createAppointmentEncounterLinks(
    encounter: Encounter,
    calendarEventId: string
  ) {
    if (this.clinicalRecord.openEncounter) {
      await this.booking.addAppointmentEncounters({
        calendarEventId,
        encounterId: encounter.id,
        providerId: encounter.userId,
        businessRole: encounter.businessRole,
        visitDateTime: encounter.visitDateTime
      });
    }
  }

  loadExtraClinicalRecordItems = async (clinicalRecord: ClinicalRecord) => {
    await Promise.all([
      clinicalRecord
        .loadCalendarEvent()
        .then(() =>
          Promise.all([
            clinicalRecord.calendarEvent?.loadEpisodeOfCare(),
            clinicalRecord.loadInvoiceItems()
          ])
        ),
      clinicalRecord.loadLinkedEncounters(),
      clinicalRecord.loadPatientNotices()
    ]);
  };

  setInjuryTab = async () => {
    const { patientRecordTab: currentTab } = this.clinicalRecord;

    if (currentTab) {
      const accTab = currentTab.allTabs.find(
        t => t.type === AccClinicalTab.Injury
      );

      if (
        this.core.hasPermissions(Permission.ClaimWrite) &&
        !this.isViewOnly &&
        this.clinicalRecord.openEncounter?.type !==
          EncounterType.RecordUpdate &&
        this.clinicalRecord.episodeOfCare
      ) {
        const claimEpisodesOfCare = await this.acc.getClaimEpisodesOfCare({
          episodesOfCare: [this.clinicalRecord.episodeOfCare.id]
        });

        if (claimEpisodesOfCare.length > 0) {
          setClaimTab(this.clinicalRecord, AccClinicalTab.Injury);
        }
      } else if (accTab) {
        currentTab.removeTab(accTab.id);

        if (!this.clinicalRecord.patientRecordTab?.activeTab) {
          this.clinical.ui.setPatientClinicalContent({
            clinicalRecord: this.clinicalRecord,
            type: PermanentClinicalTab.TodaysNotes
          });
        }
      }
    }
  };

  setSOTAPTab = async () => {
    if (this.core.tenantDetails?.country === Country.NewZealand) {
      const clinicalView = await this.userExperience.userSettingMap?.get(
        this.core.userId
      )?.clinicalView;

      const { patientRecordTab: currentTab } = this.clinicalRecord;

      const followOnEncounter = this.clinicalRecord.isFollowOnEncounter;
      const isClinicalWorkFlow =
        !!clinicalView && !!clinicalView.clinicalWorkflow;

      if (currentTab) {
        const sotapTab = currentTab.allTabs.find(
          t => t.type === PermanentClinicalTab.SOTAP
        );

        if (
          this.clinicalRecord.openEncounter?.type !==
            EncounterType.RecordUpdate &&
          this.clinicalRecord.episodeOfCare
        ) {
          if (
            this.core.hasPermissions(Permission.SOTAPInitialEncounter) &&
            !this.isViewOnly
          ) {
            if (
              !isClinicalWorkFlow ||
              clinicalView?.clinicalWorkflow === ClinicalWorkflowFormat.SOTAP
            ) {
              if (
                !followOnEncounter ||
                (followOnEncounter &&
                  this.core.hasPermissions(Permission.SOTAPFollowOn))
              ) {
                if (!sotapTab) {
                  const sotapTab = new ClinicalTabItem({
                    type: PermanentClinicalTab.SOTAP,
                    title: ClinicalDataTypeName[PermanentClinicalTab.SOTAP]
                  });

                  currentTab.insertTab(sotapTab);

                  const defaultActiveTab = clinicalView?.defaultDockView;
                  if (
                    defaultActiveTab &&
                    defaultActiveTab === PermanentClinicalTab.SOTAP
                  ) {
                    currentTab.setActive(PermanentClinicalTab.SOTAP);
                  }
                }
              }
            }
          }
        } else if (sotapTab) {
          currentTab.removeTab(sotapTab.id);

          if (!this.clinicalRecord.patientRecordTab?.activeTab) {
            this.clinical.ui.setPatientClinicalContent({
              clinicalRecord: this.clinicalRecord,
              type: PermanentClinicalTab.TodaysNotes
            });
          }
        }
      }
    }
  };

  static getGroupedListItems = (treeData: ThematicButtonData[]) => {
    let startIndex = 0;
    const items = [];
    const groups: IGroup[] = [];
    for (const group of treeData) {
      groups.push({
        key: group.key,
        name: group.name,
        count: group.items.length,
        startIndex
      });
      for (const item of group.items) {
        items.push(item);
        startIndex += 1;
      }
    }
    return {
      groups,
      items
    };
  };

  @observable showEncounterExistsPrompt = false;

  @action
  setShowEncounterExistsPrompt = (showPrompt: boolean) => {
    this.showEncounterExistsPrompt = showPrompt;
  };

  @observable isFinalising: boolean = false;

  @action setIsFinalising = (value: boolean) => {
    this.isFinalising = value;
  };

  discardEncounter = async (
    values: EncounterDiscardFormValues,
    clinicalRecord: ClinicalRecord
  ) => {
    const { reasonForDiscard, reasonForDiscardComment } = values;
    if (clinicalRecord.openEncounter) {
      const encounterId = clinicalRecord.openEncounter.id;
      clinicalRecord.encounterDiscardedByUser = true;
      await this.root.clinical.deleteEncounter(
        encounterId,
        reasonForDiscard,
        reasonForDiscardComment
      );

      // ⚠ ️It is important that DiscardConfirmationDialog MUST be dismissed before clinicalRecord closing.
      this.clinical.ui.closeDiscardConfirmationDialog();
      await clinicalRecord.close();
    }
  };

  getLatestActiveDischargeClinicalDataItem = (
    businessRoleCode: string | undefined,
    dataItems: DischargeDataItemDto[] | undefined
  ) => {
    const filteredDataItems = dataItems
      ?.filter(
        x =>
          (this.multiProviderEnabled &&
            x.businessRoleCode === businessRoleCode) ||
          !this.multiProviderEnabled
      )
      .sort(sortByDischargeItemCreatedDateDesc);

    let dischargeDataItem: DischargeDataItemDto | undefined;

    if (filteredDataItems) {
      if (filteredDataItems.length === 1) {
        dischargeDataItem = filteredDataItems[0];
      } else if (filteredDataItems.length > 1) {
        const nonReverseDischargeExists = filteredDataItems?.some(
          x => x.dischargeStatus !== DischargeStatus.Reversed
        );

        dischargeDataItem = filteredDataItems?.find(
          x =>
            (nonReverseDischargeExists &&
              x.dischargeStatus !== DischargeStatus.Reversed) ||
            !nonReverseDischargeExists
        );
      }
    }

    if (!dischargeDataItem) {
      dischargeDataItem = {
        businessRoleCode
      };
    }

    return dischargeDataItem;
  };

  @observable selectedPreviousEncounterId: string | undefined;

  @action
  setSelectedPreviousEncounterId = (value: string) => {
    this.selectedPreviousEncounterId = value;
  };

  onExportToEditorButtonClick = async (options: {
    patientId: string;
    episodeOfCareId: string;
    claimId: string | undefined;
    claimNumber: string | undefined;
    diagnosis: string | undefined;
    encounters: ConditionConsultEncounterItem[];
  }) => {
    const { document, notification, correspondence } = this.root;
    const {
      claimId,
      episodeOfCareId,
      patientId,
      claimNumber,
      diagnosis,
      encounters
    } = options;

    const activeEncounterId = this.clinicalRecord.openEncounter?.id;

    const documentName = this.getDocumentNameForExport(claimNumber, diagnosis);

    const documentId = newGuid();

    try {
      const templates = await document.getTemplates({
        name: "Condition consults export"
      });

      const consultIds: string = encounters
        .filter(encounter => encounter.selected)
        .map(encounter => encounter.id)
        .join(", ");

      const context = this.getDocumentTemplateContext(
        consultIds,
        {
          userId: this.core.userId,
          episodeOfCareId,
          patientId,
          claimId
        },
        true
      );

      const documentTemplate: TemplateRenderDto = await document.renderTemplate(
        templates.length ? templates[0].id : "",
        {
          contentType: DocumentContentType.Sfdt,
          skipMerge: false,
          isPreview: false,
          context
        }
      );

      const metadata: DocumentMetadataItem[] = [
        { key: DocumentEnum.Extension, value: "pdf" },
        { key: DocumentEnum.ContentType, value: DocumentContentType.Sfdt },
        {
          key: DocumentEnum.Name,
          value: documentName
        },
        { key: DocumentEnum.EncounterId, value: activeEncounterId },
        { key: DocumentEnum.Date, value: DateTime.now().toISODate() }
      ];

      const dto: DocumentDto = {
        id: documentId,
        patientId,
        from: this.core.userId,
        type: CorrespondenceType.Report,
        direction: CorrespondenceDirection.Out,
        status: CorrespondenceStatus.Draft,
        content: documentTemplate.content,
        showOnTimeline: true,
        metadata,
        eTag: ""
      };

      correspondence.mergeCorrespondence(dto);
      correspondence.activeDocumentTab = {
        documentId,
        patientId,
        encounterId: activeEncounterId
      };
    } catch {
      notification.error("An error occurred - unable to export to editor");
    }
  };
  onExportToPdfButtonClick = async (options: {
    patientId: string;
    episodeOfCareId: string;
    claimId: string | undefined;
    claimNumber: string | undefined;
    diagnosis: string | undefined;
    encounters: ConditionConsultEncounterItem[];
  }) => {
    const { document, notification, correspondence, clinical } = this.root;
    const {
      claimId,
      episodeOfCareId,
      patientId,
      claimNumber,
      diagnosis,
      encounters
    } = options;

    const activeEncounterId = this.clinicalRecord.openEncounter?.id;

    if (!activeEncounterId) {
      return;
    }

    const documentName = this.getDocumentNameForExport(claimNumber, diagnosis);

    const documentId = newGuid();

    try {
      const templates = await document.getTemplates({
        name: "Condition consults export"
      });

      const consultIds: string = encounters
        .map(encounter => encounter.id)
        .join(", ");

      const context = this.getDocumentTemplateContext(
        consultIds,
        {
          userId: this.core.userId,
          episodeOfCareId,
          patientId,
          claimId
        },
        false
      );

      const documentTemplate: TemplateRenderDto = await document.renderTemplate(
        templates.length ? templates[0].id : "",
        {
          contentType: DocumentContentType.Pdf,
          skipMerge: false,
          isPreview: false,
          context
        }
      );

      const documentContent: AddDocumentDto = { metadata: [] };
      const { Extension, ContentType, Name, EncounterId, Date } = DocumentEnum;
      const getMetadata = () =>
        getNewMetadata(documentContent.metadata, {
          [Extension]: "pdf",
          [ContentType]: DocumentContentType.ApplicationPdf,
          [Name]: documentName,
          [EncounterId]: activeEncounterId,
          [Date]: DateTime.now().toISODate()
        });

      documentContent.patientId = patientId;
      documentContent.from = this.core.userId;
      documentContent.content = documentTemplate.content;
      documentContent.metadata = getMetadata();
      documentContent.store = StoreType.Correspondence;
      documentContent.type = CorrespondenceType.Report;
      documentContent.direction = CorrespondenceDirection.Out;
      documentContent.showOnTimeline = true;
      documentContent.id = documentId;

      const options: DocumentCreateOptions = {
        source: DocumentSource.Content,
        documents: [documentContent]
      };

      await correspondence.addDocuments(activeEncounterId, options);
      clinical.ui.setConditionConsultsDocumentId(documentId);
      clinical.ui.setConditionConsultsExportDialogVisible(false);
    } catch {
      notification.error("An error occurred - unable to export to PDF");
    }
  };

  getDocumentTemplateContext = (
    consultIds: string,

    contextIds: {
      patientId: string;
      episodeOfCareId: string;
      claimId?: string;
      userId: string;
    },
    documentEdited: boolean
  ): TemplateRenderOptions["context"] => {
    const { episodeOfCareId, patientId, claimId, userId } = contextIds;

    const baseContext = {
      PatientId: patientId,
      EpisodeOfCareId: episodeOfCareId,
      DocumentEdited: documentEdited,
      ConsultIds: consultIds,
      UserId: userId
    };

    return claimId
      ? {
          ...baseContext,
          ClaimId: claimId
        }
      : baseContext;
  };

  getDocumentNameForExport = (
    claimNumber: string | undefined,
    diagnosis: string = "undiagnosed"
  ) => {
    return claimNumber
      ? `Export of consultation notes - ${diagnosis} - ${claimNumber}`
      : `Export of consultation notes - ${diagnosis}`;
  };

  getInitialItems = async (
    patientId: string,
    episodeOfCareId: string
  ): Promise<ConditionConsultEncounterItem[]> => {
    const eocEncounters = await this.root.clinical.getEncounters({
      patientId,
      episodeOfCareIds: [episodeOfCareId]
    });

    const sortedEncounters = Array.from(eocEncounters).sort((a, b) =>
      compareDatesPredicate(b.startDateTime, a.startDateTime)
    );

    return sortedEncounters.map((x, index, array) => {
      return {
        label: `${ordinalNumber(array.length - index)} - ${formatCalendarDate(
          x.startDateTime
        )} with ${x.user?.fullName}`,
        id: x.id,
        selected: true
      };
    });
  };

  public loadHelper = async (trackEvent: (eventData: object) => void) => {
    await Promise.all([
      this.clinicalRecord
        .loadCalendarEvent()
        .then(() =>
          Promise.all([
            this.clinicalRecord.calendarEvent?.loadEpisodeOfCare(),
            this.clinicalRecord.loadInvoiceItems()
          ])
        ),
      this.clinicalRecord.loadLinkedEncounters(),
      this.clinicalRecord.loadPatientNotices(),
      this.clinicalRecord.loadEpisodeOfCare(),
      this.clinicalRecord.loadPatientEpisodesOfCare(),
      this.clinicalRecord.loadClinicalData(),
      this.clinicalRecord.encounterId
        ? this.clinicalRecord.loadStructuredNotes(
            this.clinicalRecord.encounterId,
            this.userExperience.settings.ClinicalNotesFormat
          )
        : undefined
    ]);

    if (!this.showEncounterExistsPrompt) {
      await this.initialiseEncounter(trackEvent);
    }
  };

  initialiseEncounter = async (trackEvent: (eventData: object) => void) => {
    const { clinicalRecord } = this;
    this.clinical.ui.updateOpenedRecordIds(this.patientId, this.encounterId);

    await Promise.all([
      this.trackEncounterStartedEvent(trackEvent),
      !this.isViewOnly
        ? this.clinicalRecord.updateReasonForVisitFromDiagnosis()
        : undefined,
      this.setSOTAPTab(),
      this.setInjuryTab(),
      this.root.document.getAutofills(),
      clinicalRecord.isClearingInvoiceItemsRequired && !this.isViewOnly
        ? clinicalRecord.updateInvoice([])
        : undefined
    ]);
  };

  get canLinkPrimaryDiagnosis() {
    const claim = this.clinicalRecord.episodeOfCare?.claim;
    return [
      ClaimStatuses.Incomplete,
      ClaimStatuses.Ready,
      ClaimStatuses.Error,
      ClaimStatuses.NotVerified,
      ClaimStatuses.Private,
      ClaimStatuses.Declined,
      undefined
    ].includes(claim?.claimStatus as ClaimStatuses);
  }

  public checkDiagnoseEditability = (episodeOfCareId: string | undefined) => {
    const isMedicalHistoryLinked = episodeOfCareId
      ? episodeOfCareId === this.clinicalRecord.openEncounter?.episodeOfCareId
      : false;

    return this.core.hasPermissions(
      Permission.MedicalHistoryDiagnosisSyncAllowed
    )
      ? this.canLinkPrimaryDiagnosis || !isMedicalHistoryLinked
      : true;
  };

  public navigateAway = () => {
    this.clinical.ui.setActiveRecord(undefined);
  };
}
